/**
 * AnimationUtils - 共享动画工具函数
 * 玩家、NPC、敌人移动时的 squash/bounce/rotate 动画
 */
import { globalTweens, Easing } from 'agent-gamedev';
import type { TransformComponent } from 'agent-gamedev';

/**
 * 移动时的挤压拉伸+弹跳+旋转动画
 * 与位置移动 tween 配合使用（本函数不控制 x/y 位置）
 */
export function animateMoveTransform(
  transform: TransformComponent,
  targetY: number,
  dc: number,
  dr: number,
  duration: number
): void {
  if (dc !== 0 && dr === 0) {
    // 左右移动：弹跳 + 挤压拉伸 + 倾斜
    globalTweens.to(transform, { y: targetY - 5 }, { duration: duration * 0.35, easing: Easing.easeOutQuad });
    globalTweens.to(transform, { y: targetY }, { duration: duration * 0.65, easing: Easing.easeOutSine, delay: duration * 0.35 });

    globalTweens.to(transform, { scaleX: 0.8, scaleY: 1.15 }, { duration: duration * 0.25, easing: Easing.easeOutQuad });
    globalTweens.to(transform, { scaleX: 1.06, scaleY: 0.96 }, { duration: duration * 0.35, easing: Easing.easeInOutQuad, delay: duration * 0.25 });
    globalTweens.to(transform, { scaleX: 1.0, scaleY: 1.0 }, { duration: duration * 0.4, easing: Easing.easeOutQuad, delay: duration * 0.6 });

    const rotDir = dc < 0 ? -0.14 : 0.14;
    globalTweens.to(transform, { rotation: rotDir }, { duration: duration * 0.3, easing: Easing.easeOutQuad });
    globalTweens.to(transform, { rotation: 0 }, { duration: duration * 0.7, easing: Easing.easeOutSine, delay: duration * 0.3 });
  } else if (dr !== 0 && dc === 0) {
    // 上下移动：zoom in/out + 扭动
    globalTweens.to(transform, { scaleX: 1.14, scaleY: 1.14 }, { duration: duration * 0.25, easing: Easing.easeOutQuad });
    globalTweens.to(transform, { scaleX: 0.94, scaleY: 0.94 }, { duration: duration * 0.3, easing: Easing.easeInQuad, delay: duration * 0.25 });
    globalTweens.to(transform, { scaleX: 1.0, scaleY: 1.0 }, { duration: duration * 0.45, easing: Easing.easeOutSine, delay: duration * 0.55 });

    const wiggle = dr < 0 ? 0.1 : -0.1;
    globalTweens.to(transform, { rotation: wiggle }, { duration: duration * 0.2, easing: Easing.easeOutQuad, yoyo: true, repeat: 1 });
    globalTweens.to(transform, { rotation: 0 }, { duration: duration * 0.4, easing: Easing.easeOutQuad, delay: duration * 0.4 });
  } else {
    // 对角线：简单呼吸
    globalTweens.to(transform, { scaleX: 1.08, scaleY: 1.08 }, { duration: duration * 0.3, easing: Easing.easeOutQuad });
    globalTweens.to(transform, { scaleX: 1.0, scaleY: 1.0 }, { duration: duration * 0.7, easing: Easing.easeOutSine, delay: duration * 0.3 });
  }
}

export function animateActorStepTransform(
  transform: TransformComponent,
  targetX: number,
  targetY: number,
  dc: number,
  dr: number,
  duration: number
): void {
  globalTweens.killTweensOf(transform);

  const overshootX = targetX + dc * 3;
  const overshootY = targetY + dr * 3;
  const lean = dc !== 0 ? dc * 0.12 : dr * -0.06;

  globalTweens.to(transform, { x: overshootX, y: overshootY }, {
    duration: duration * 0.78,
    easing: Easing.easeInOutCubic,
  });
  globalTweens.to(transform, { x: targetX, y: targetY }, {
    duration: duration * 0.22,
    easing: Easing.easeOutBack,
    delay: duration * 0.78,
  });

  if (dc !== 0) {
    globalTweens.to(transform, { scaleX: 0.86, scaleY: 1.12, rotation: lean }, {
      duration: duration * 0.28,
      easing: Easing.easeOutQuad,
    });
    globalTweens.to(transform, { scaleX: 1.08, scaleY: 0.95, rotation: lean * 0.45 }, {
      duration: duration * 0.34,
      easing: Easing.easeInOutQuad,
      delay: duration * 0.28,
    });
  } else {
    globalTweens.to(transform, { scaleX: 1.08, scaleY: 0.93, rotation: lean }, {
      duration: duration * 0.24,
      easing: Easing.easeOutQuad,
    });
    globalTweens.to(transform, { scaleX: 0.96, scaleY: 1.08, rotation: lean * 0.5 }, {
      duration: duration * 0.34,
      easing: Easing.easeInOutQuad,
      delay: duration * 0.24,
    });
  }
  globalTweens.to(transform, { scaleX: 1, scaleY: 1, rotation: 0 }, {
    duration: duration * 0.38,
    easing: Easing.easeOutBack,
    delay: duration * 0.62,
  });
}

export function animatePushedBlockTransform(
  transform: TransformComponent,
  targetX: number,
  targetY: number,
  dc: number,
  dr: number,
  duration: number
): void {
  globalTweens.killTweensOf(transform);

  const impactScaleX = dc !== 0 ? 0.88 : 1.08;
  const impactScaleY = dr !== 0 ? 0.88 : 1.08;
  const stretchScaleX = dc !== 0 ? 1.08 : 0.98;
  const stretchScaleY = dr !== 0 ? 1.08 : 0.98;
  const wobble = dc !== 0 ? dc * 0.055 : dr * -0.055;

  globalTweens.to(transform, { scaleX: impactScaleX, scaleY: impactScaleY, rotation: -wobble }, {
    duration: duration * 0.16,
    easing: Easing.easeOutQuad,
  });
  globalTweens.to(transform, { x: targetX, y: targetY, scaleX: stretchScaleX, scaleY: stretchScaleY, rotation: wobble }, {
    duration: duration * 0.68,
    easing: Easing.easeOutCubic,
    delay: duration * 0.08,
  });
  globalTweens.to(transform, { scaleX: 1, scaleY: 1, rotation: 0 }, {
    duration: duration * 0.24,
    easing: Easing.easeOutBack,
    delay: duration * 0.76,
  });
}
